import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useRef, useState } from "react";

import { useInvalidateRecurringRequestPreview } from "@/features/RecurringRequests/RecurringRequestPreviewProvider";
import { recurrenceInputFromPersistedSeries } from "@/features/RecurringRequests/recurrence";
import { RecurrenceAvailabilityPreview } from "@/features/RecurringRequests/types";
import { useMutationWithError } from "@/utils/hooks/useMutationWithError";
import { useFlags } from "@/utils/launchDarkly";
import {
  Button,
  Radio,
  RadioGroup,
} from "@whoosh/design-system/src/components/controls";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import {
  Box,
  HStack,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { RecurringRequestDatesModal } from "../../../../../BookingSummary/components/RecurringRequestSeriesCard/components/RecurringRequestDatesModal/RecurringRequestDatesModal";
import { canBookingSaveAtom } from "../../../../atoms/selectors/canBookingSaveAtom/canBookingSaveAtom";
import { isFormDirtyAtom } from "../../../../atoms/selectors/isFormDirtyAtom/isFormDirtyAtom";
import {
  currentBookingAtom,
  originalBookingAtom,
  originalBookingOwnerClientIdAtom,
} from "../../../../atoms/stores";
import { useSaveChangesToLessonBooking } from "../../../../hooks/saveChangesHooks/useSaveChangesToLessonBooking/useSaveChangesToLessonBooking";
import { useSaveChangesToMultiSlotBooking } from "../../../../hooks/saveChangesHooks/useSaveChangesToMultiSlotBooking/useSaveChangesToMultiSlotBooking";
import { RecurringRequestPreviewConfirmation } from "../../../../hooks/saveChangesHooks/useSaveChangesToSingleSlotBooking/useSaveChangesToSingleSlotBooking";
import { useSaveChangesToSingleSlotBooking } from "../../../../hooks/saveChangesHooks/useSaveChangesToSingleSlotBooking/useSaveChangesToSingleSlotBooking";
import type { PatronEditBooking } from "../../../../types";
import { ActionButtonsPreviewMutation } from "./__generated__/ActionButtonsPreviewMutation.graphql";
import type { RoundType } from "./__generated__/ActionButtonsPreviewMutation.graphql";
import { getEditRequestSubmitMode } from "./getEditRequestSubmitMode";
import { stitchRecurringRequestConfirmPreview } from "./stitchRecurringRequestConfirmPreview";

type Props = {
  onClose: () => void;
  onSaveSuccess?: () => void;
  isDurationBasedFacility: boolean;
  isLessonBooking: boolean;
};

type RecurringRequestEditScope = "THIS_ONLY" | "THIS_AND_FOLLOWING";

type ConfirmPreview = {
  reviewedPlanToken: string;
  unchangedOccurrences: RecurrenceAvailabilityPreview["included"];
  availableOccurrences: RecurrenceAvailabilityPreview["included"];
  unavailableDates: RecurrenceAvailabilityPreview["excluded"];
  removedOccurrences: Array<{ date: string }>;
};

export const ActionButtons = ({
  onClose,
  onSaveSuccess,
  isDurationBasedFacility,
  isLessonBooking,
}: Props) => {
  const { patronRecurringRequests } = useFlags();
  const { showToast } = useToast();
  const isFormDirty = useAtomValue(isFormDirtyAtom);
  const canBookingSave = useAtomValue(canBookingSaveAtom);
  const originalBooking = useAtomValue(originalBookingAtom);
  const currentBooking = useAtomValue(currentBookingAtom);
  const originalOwnerClientId = useAtomValue(originalBookingOwnerClientIdAtom);
  const recurringRequestSeries = originalBooking?.recurringRequestSeries;
  const selectedOccurrenceDate = recurringRequestSeries?.sourceDate;
  const [isScopeModalOpen, setIsScopeModalOpen] = useState(false);
  const [isDatesConfirmOpen, setIsDatesConfirmOpen] = useState(false);
  const [confirmPreview, setConfirmPreview] = useState<ConfirmPreview | null>(
    null
  );
  const [recurringRequestEditScope, setRecurringRequestEditScope] =
    useState<RecurringRequestEditScope>("THIS_ONLY");
  const shouldReopenScopeOnDatesCloseRef = useRef(true);
  const submitMode = getEditRequestSubmitMode({
    isLessonBooking,
    isUnsavedSeriesPreview: !!originalBooking?.isUnsavedSeriesPreview,
    isRecurringRequest: !!originalBooking?.isRecurringRequest,
  });
  const hasRecurringScheduleFieldChanges =
    isDurationBasedFacility &&
    !!originalBooking?.isRecurringRequest &&
    !originalBooking.isUnsavedSeriesPreview &&
    ((currentBooking?.startSlotId ?? null) !==
      (originalBooking.startSlotId ?? null) ||
      (currentBooking?.durationMins ?? null) !==
        (originalBooking.durationMins ?? null));
  const effectiveSubmitMode = hasRecurringScheduleFieldChanges
    ? "SAVE_IMMEDIATELY"
    : submitMode;
  // Saving drafts or queue-backed requests returns to their summary. Saved
  // requests can have CONFIRMED status without being assigned to a booking slot.
  const invalidatePreview = useInvalidateRecurringRequestPreview();
  const closeAfterSave =
    submitMode === "RETURN_TO_PREVIEW" ||
    originalBooking?.status === "DRAFT" ||
    originalBooking?.isRequestBooking
      ? onClose
      : onSaveSuccess ?? onClose;

  const handleSaveSuccess = ({
    hasPostCommitWarning = false,
  }: PatronEditBooking.SaveSuccessOptions = {}) => {
    invalidatePreview?.();
    closeAfterSave();
    if (
      originalBooking?.isRequestBooking &&
      originalBooking.status === "CONFIRMED" &&
      !hasPostCommitWarning
    ) {
      showToast({ title: t`Changes saved`, semanticStyle: "success" });
    }
  };

  const [handleSaveSingleSlotBooking, isSavingSingleSlotChanges] =
    useSaveChangesToSingleSlotBooking(handleSaveSuccess);
  const [handleSaveMultiSlotBooking, isSavingMultiSlotChanges] =
    useSaveChangesToMultiSlotBooking(handleSaveSuccess);
  const [handleSaveLessonBooking, isSavingLessonChanges] =
    useSaveChangesToLessonBooking(handleSaveSuccess);
  const [previewSeries, isPreviewing] =
    useMutationWithError<ActionButtonsPreviewMutation>(
      ActionButtonsPreviewMutationString
    );

  const isSavingChanges =
    isSavingSingleSlotChanges ||
    isSavingMultiSlotChanges ||
    isSavingLessonChanges;

  const closeScopeModal = () => {
    setIsScopeModalOpen(false);
    setRecurringRequestEditScope("THIS_ONLY");
  };

  const saveChanges = (
    editScope: RecurringRequestEditScope,
    previewConfirmation?: RecurringRequestPreviewConfirmation
  ) => {
    if (isDurationBasedFacility) {
      handleSaveMultiSlotBooking(editScope, previewConfirmation);
    } else {
      handleSaveSingleSlotBooking(editScope, previewConfirmation);
    }
  };

  const previewThenConfirmDates = () => {
    const series = recurringRequestSeries;
    const recurrence = recurrenceInputFromPersistedSeries(
      series,
      selectedOccurrenceDate
    );
    const request = currentBooking?.request;

    if (!originalBooking?.id || !recurrence || !request) {
      closeScopeModal();
      saveChanges("THIS_AND_FOLLOWING");
      return;
    }

    const recurrenceInput =
      recurrence.endCondition === "ON_DATE"
        ? {
            recurrenceType: recurrence.recurrenceType,
            repeatInterval: recurrence.repeatInterval,
            endCondition: "ON_DATE" as const,
            endDate: recurrence.endDate,
          }
        : {
            recurrenceType: recurrence.recurrenceType,
            repeatInterval: recurrence.repeatInterval,
            endCondition: "AFTER_OCCURRENCES" as const,
            occurrenceCount: recurrence.occurrenceCount ?? 1,
          };

    const sourceArgs = currentBooking
      ? {
          type: isLessonBooking ? ("LESSON" as const) : ("STANDARD" as const),
          ownerMemberId:
            currentBooking.rounds.find((round) => round.isOwner)?.player.id ??
            originalOwnerClientId,
          durationMins: currentBooking.durationMins ?? null,
          rounds: currentBooking.rounds.map((round) => {
            const type = previewRoundType(round.player.clientType);

            return {
              type,
              playerId: round.player.id,
              bookingIndex: round.bookingIndex,
              hostId: round.hostId ?? null,
              rateId:
                round.type === "Golf"
                  ? round.roundRate?.id ?? null
                  : round.rate?.id ?? null,
              courseLayoutId:
                round.type === "Golf" ? round.courseLayout?.id ?? null : null,
              transportationTypeId:
                round.type === "Golf"
                  ? round.transportationType?.id ?? null
                  : null,
              secondaryTransportationTypeId:
                round.secondaryTransportationType?.id ?? null,
              nonPlayingHost:
                type === "MEMBER" ? round.isNonPlayingHost ?? false : null,
            };
          }),
        }
      : undefined;

    previewSeries({
      variables: {
        input: {
          sourceBookingId: originalBooking.id,
          recurrence: recurrenceInput,
          request: {
            time: request.time || "",
            minTime: request.minTime || "",
            maxTime: request.maxTime || "",
          },
          sourceArgs,
        },
      },
      onCompleted: (response) => {
        const payload = response.responsePayload;
        if (!payload?.success || !payload.reviewedPlanToken) return;

        const stitched = stitchRecurringRequestConfirmPreview({
          sourceDate: series?.sourceDate ?? "",
          seriesBookingDates: series?.seriesBookingDates ?? [],
          included:
            payload.included?.map((occurrence) => ({
              date: occurrence.date,
              warnings:
                occurrence.warnings?.map((warning) => ({
                  type: warning.type,
                  label: warning.label,
                  startAt: warning.startAt,
                  endAt: warning.endAt,
                })) ?? [],
            })) ?? [],
          excluded:
            payload.excluded?.map((exclusion) => ({
              date: exclusion.date,
              reason: exclusion.reason,
              requestOpensDate: exclusion.requestOpensDate,
              conflict: exclusion.conflict
                ? {
                    type: exclusion.conflict.type,
                    label: exclusion.conflict.label,
                    startAt: exclusion.conflict.startAt,
                    endAt: exclusion.conflict.endAt,
                  }
                : null,
            })) ?? [],
          proposedRemovals:
            payload.proposedRemovals?.map((removal) => ({
              date: removal.date,
            })) ?? [],
        });

        setConfirmPreview({
          reviewedPlanToken: payload.reviewedPlanToken,
          ...stitched,
        });
        setIsScopeModalOpen(false);
        setIsDatesConfirmOpen(true);
      },
    });
  };

  const handleConfirmScope = () => {
    const editScope = recurringRequestEditScope;

    if (editScope === "THIS_AND_FOLLOWING" && patronRecurringRequests) {
      previewThenConfirmDates();
      return;
    }

    closeScopeModal();
    saveChanges(editScope);
  };

  const handleBackFromDatesConfirm = () => {
    setIsDatesConfirmOpen(false);
    setConfirmPreview(null);
    if (shouldReopenScopeOnDatesCloseRef.current) {
      setIsScopeModalOpen(true);
    }
    shouldReopenScopeOnDatesCloseRef.current = true;
  };

  const handleConfirmDates = () => {
    if (!confirmPreview) return;

    shouldReopenScopeOnDatesCloseRef.current = false;
    closeScopeModal();
    setIsDatesConfirmOpen(false);
    saveChanges("THIS_AND_FOLLOWING", {
      reviewedPlanToken: confirmPreview.reviewedPlanToken,
    });
  };

  const willPreviewDates =
    patronRecurringRequests &&
    recurringRequestEditScope === "THIS_AND_FOLLOWING";

  const seriesRecurrence = recurrenceInputFromPersistedSeries(
    recurringRequestSeries,
    selectedOccurrenceDate
  );

  return isFormDirty ? (
    <>
      <HStack
        padding={5}
        gap={4}
        justifyContent="space-between"
        borderTopWidth={1}
        borderColor="outline-default"
        backgroundColor="surface-primary"
      >
        <Button
          variant="secondary"
          onPress={onClose}
          accessibilityLabel={t`Cancel`}
          testID="edit-booking-cancel-button"
        >
          {t`Cancel`}
        </Button>
        <Button
          accessibilityLabel={
            effectiveSubmitMode === "CHOOSE_SCOPE"
              ? t`Continue to choose which requests to update`
              : effectiveSubmitMode === "RETURN_TO_PREVIEW"
              ? t`Continue to request series preview`
              : t`Save Changes button`
          }
          testID="edit-booking-save-button"
          isDisabled={!canBookingSave}
          isLoading={isSavingChanges}
          onPress={() => {
            if (isLessonBooking) {
              handleSaveLessonBooking();
            } else if (effectiveSubmitMode === "CHOOSE_SCOPE") {
              setIsScopeModalOpen(true);
            } else if (isDurationBasedFacility) {
              handleSaveMultiSlotBooking();
            } else {
              handleSaveSingleSlotBooking();
            }
          }}
        >
          {effectiveSubmitMode === "SAVE_IMMEDIATELY"
            ? t`Save Changes`
            : t`Continue`}
        </Button>
      </HStack>

      <StandardModal
        isOpen={isScopeModalOpen}
        onClose={closeScopeModal}
        upperTitle={t`Update recurring request`}
        hideFooterPadding
        content={
          <VStack paddingHorizontal={6} paddingVertical={4}>
            <RadioGroup
              value={recurringRequestEditScope}
              onChange={(value) =>
                setRecurringRequestEditScope(
                  value === "THIS_AND_FOLLOWING"
                    ? "THIS_AND_FOLLOWING"
                    : "THIS_ONLY"
                )
              }
              accessibilityLabel={t`Choose which recurring requests to update`}
            >
              <Box paddingBottom={1}>
                <Radio
                  value="THIS_ONLY"
                  label={t`This request only`}
                  accessibilityLabel={t`Update this request only`}
                  testID="edit-recurring-request-this-only-radio"
                />
              </Box>
              <Radio
                value="THIS_AND_FOLLOWING"
                label={t`This and following requests`}
                accessibilityLabel={t`Update this and following requests`}
                testID="edit-recurring-request-this-and-following-radio"
              />
            </RadioGroup>
          </VStack>
        }
        footerContent={
          <HStack
            gap={4}
            justifyContent="space-between"
            padding={5}
            borderTopWidth={1}
            borderColor="outline-default"
            backgroundColor="surface-primary"
          >
            <Button
              variant="white"
              size="small"
              accessibilityLabel={t`Back to edit request`}
              testID="edit-recurring-request-back-button"
              onPress={closeScopeModal}
            >
              {t`Back`}
            </Button>
            <Button
              size="small"
              accessibilityLabel={
                willPreviewDates
                  ? t`Continue to recurring request dates`
                  : t`Confirm recurring request update`
              }
              testID="edit-recurring-request-confirm-button"
              isLoading={isPreviewing}
              onPress={handleConfirmScope}
            >
              {willPreviewDates ? t`Continue` : t`Confirm`}
            </Button>
          </HStack>
        }
      />

      <RecurringRequestDatesModal
        mode="confirm"
        request={currentBooking?.request}
        unchangedRequest={originalBooking?.request}
        recurrenceType={seriesRecurrence?.recurrenceType}
        repeatInterval={seriesRecurrence?.repeatInterval}
        availableOccurrences={confirmPreview?.availableOccurrences ?? []}
        unavailableDates={confirmPreview?.unavailableDates ?? []}
        removedOccurrences={confirmPreview?.removedOccurrences ?? []}
        unchangedOccurrences={confirmPreview?.unchangedOccurrences ?? []}
        isOpen={isDatesConfirmOpen}
        onClose={handleBackFromDatesConfirm}
        onBack={handleBackFromDatesConfirm}
        onConfirm={handleConfirmDates}
        isConfirming={isSavingChanges}
      />
    </>
  ) : null;
};

const previewRoundType = (
  clientType: PatronEditBooking.RoundPlayer["clientType"]
): RoundType => {
  switch (clientType) {
    case "member":
    case "memberHold":
      return "MEMBER" as const;
    case "customer":
    case "unknownCustomer":
      return "CUSTOMER" as const;
    case "guest":
    case "unknownGuest":
    case "newGuest":
      return "GUEST" as const;
  }

  throw new Error(`Unsupported preview round client type: ${clientType}`);
};

const ActionButtonsPreviewMutationString = graphql`
  mutation ActionButtonsPreviewMutation(
    $input: PreviewPatronRequestSeriesInput!
  ) {
    responsePayload: previewPatronRequestSeries(input: $input) {
      success
      reviewedPlanToken
      errors {
        field
        code
        message
      }
      included {
        date
        warnings {
          type
          label
          startAt
          endAt
        }
      }
      excluded {
        date
        reason
        requestOpensDate
        conflict {
          type
          label
          startAt
          endAt
        }
      }
      proposedRemovals {
        date
      }
    }
  }
`;
